// @ts-check

import { createContext, useContext, useEffect, useState } from "react";
import { Component, Stage } from "ngl";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/ui/toggle";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import useThemeMode from "../../hooks/useThemeMode";

const ProteinViewerDialogContext = createContext({
  /** @type {string | File | null} */
  file: null,
  /** @type {StructureRepresentation[] | null} */
  representations: null,
  /** @type {HTMLDivElement | null} */
  viewportEl: null,
  /** @type {React.Dispatch<React.SetStateAction<HTMLDivElement | null>>} */
  setViewportEl() { },
  /** @type {Stage | null} */
  stage: null,
  /** @type {React.Dispatch<React.SetStateAction<Stage | null>>} */
  setStage() { },
  /** @type {Component | null} */
  component: null,
  /** @type {React.Dispatch<React.SetStateAction<Component | null>>} */
  setComponent() { },
})

/** @typedef {React.ContextType<typeof ProteinViewerDialogContext>} ProteinViewerDialogContextValue */

function useProteinViewerDialogContext() {
  return useContext(ProteinViewerDialogContext)
}

/**
  * @typedef {Object} StructureRepresentation
  * @property {string} type
  * @property {string} select
  * @property {string} [color]
  * @property {number} [opacity]
  * @property {number} [opacity]
  * @property {boolean} [visible]
  */

const DEFAULT_COLOR = "residueindex"
const DEFAULT_COLOR_SCALE = ['blue', 'green', 'yellow', 'red']

/** @satisfies {StructureRepresentation[]} */
const DEFAULT_REPRESENTATIONS = [
  {
    type: "cartoon",
    select: "polymer",
  },
  {
    type: "ball+stick",
    select: "ligand",
  },
  {
    type: "ball+stick",
    select: "water",
    visible: false,
  }
]

/**
  * @typedef {Object} ProteinViewerDialogExtraProps
  * @property {string | File} [file]
  * @property {StructureRepresentation[]} [representations]
  */

/** @param {React.ComponentProps<typeof Dialog> & ProteinViewerDialogExtraProps} props */
function ProteinViewerDialog({ children, file, representations = DEFAULT_REPRESENTATIONS, ...props }) {
  const [stage, setStage] = useState(/**@type{Stage|null}*/(null))
  const [component, setComponent] = useState(/** @type{Component|null}*/(null))
  const [viewportEl, setViewportEl] = useState(/**@type{HTMLDivElement|null}*/(null))

  return (
    <ProteinViewerDialogContext.Provider
      value={{
        file,
        representations,
        viewportEl,
        setViewportEl,
        stage,
        setStage,
        component,
        setComponent
      }}
    >
      <Dialog {...props}>
        {children}
      </Dialog>
    </ProteinViewerDialogContext.Provider>
  )
}

/** @param {React.ComponentProps<typeof DialogTrigger>} props */
function ProteinViewerDialogTrigger({ children, ...props }) {
  return (
    <DialogTrigger {...props}>
      {children}
    </DialogTrigger>
  )
}

/** @param {React.ComponentProps<typeof DialogContent>} props */
function ProteinViewerDialogContent({ children, className, ...props }) {
  const { file, representations, viewportEl, stage, setStage, component, setComponent } = useContext(ProteinViewerDialogContext)
  const { resolvedTheme } = useThemeMode()
  const isDarkTheme = resolvedTheme === "dark"

  /** @param {HTMLDivElement} viewportEl */
  function setupStage(viewportEl) {
    if (viewportEl == null) return

    const stage = new Stage(viewportEl, {
      backgroundColor: isDarkTheme ? "#333" : "#e8dfd0", // Initial bg
    })

    setStage(stage)

    const vieportElObserver = new ResizeObserver(() => stage.handleResize())
    vieportElObserver.observe(viewportEl)

    return () => {
      stage.dispose()
      setStage(null)
      vieportElObserver.disconnect()
    }
  }

  /**
    * @param {string | File | null} file
    * @param {Stage | null} stage
    */
  function loadFileToStage(file, stage) {
    if (file == null) return
    if (stage == null) return

    const ext = file instanceof File
      ? file.name.split(".").pop()
      : (() => { try { return new URL(file).pathname.split(".").pop() } catch { return file.split(".").pop() } })()
    const controller = new AbortController()
    stage.loadFile(file, { ext })
      .then(res => {
        controller.signal.throwIfAborted()
        return res
      })
      .then(component => {
        if (!(component instanceof Component)) throw new Error("No component loaded")

        setComponent(component)

        // Delay the autoView and resize handling to ensure the component is properly rendered first
        setTimeout(() => {
          stage.handleResize()
          stage.autoView(500)
        }, 500)
      })
      .catch(error => {
        if (error?.name === "AbortError") return
        // TODO: Maybe show toast
        console.error(error)
      })

    return () => {
      controller.abort()
      stage.removeComponent(component)
      setComponent(null)
    }
  }

  /**
    * @param {Component | null} component
    * @param {StructureRepresentation[] | null} representations
    */
  function addRepresentations(component, representations) {
    if (component == null) return
    if (representations == null) return
    // see https://nglviewer.org/ngl/api/manual/snippets.html#implementing-custom-default-representations
    if (component.type !== "structure") return

    for (const representation of representations) {
      component.addRepresentation(representation.type, {
        sele: representation.select,
        color: representation.color ?? DEFAULT_COLOR,
        opacity: representation.opacity,
        colorScale: DEFAULT_COLOR_SCALE,
        visible: representation.visible,
      })
    }

    return () => {
      component.removeAllRepresentations()
    }
  }

  /** @param {StructureRepresentation[] | null} representations */
  function hashRepresentations(representations) {
    if (representations == null) return null
    return representations.map(repr => JSON.stringify(repr)).join()
  }

  useEffect(() => setupStage(viewportEl), [viewportEl])
  useEffect(() => loadFileToStage(file, stage), [file, stage])
  useEffect(() => (
    addRepresentations(component, representations)
    // We use a hash of the representations here
    // so as to not rerun the effect on every render since it is an array literal
  ), [component, hashRepresentations(representations)])

  // Update background color when theme changes
  useEffect(() => {
    if (stage == null) return
    stage.setParameters({ backgroundColor: isDarkTheme ? "#333" : "#e8dfd0" })
  }, [isDarkTheme, stage])

  return (
    <DialogContent
      className={cn(
        "w-[calc(100vw-2rem)]! h-[calc(100dvh-2rem)]! md:w-[calc(100vw-4rem)]! md:h-[calc(100dvh-4rem)]! max-w-none! grid-rows-[auto_1fr_auto]",
        className,
      )}
      {...props}
    >
      {children}
    </DialogContent>
  )
}

/** @param {React.ComponentProps<typeof DialogHeader>} props */
function ProteinViewerDialogHeader({ children, ...props }) {
  return (
    <DialogHeader {...props}>
      {children}
    </DialogHeader>
  )
}

/** @param {React.ComponentProps<typeof DialogTitle>} props */
function ProteinViewerDialogTitle({ children, ...props }) {
  return (
    <DialogTitle {...props}>
      {children}
    </DialogTitle>
  )
}

/** @param {React.ComponentProps<typeof DialogDescription>} props */
function ProteinViewerDialogDescription({ children, className, ...props }) {
  return (
    <DialogDescription className={cn("sr-only", className)} {...props}>
      {children}
    </DialogDescription>
  )
}

/** @param {React.ComponentProps<'div'>} props */
function ProteinViewerDialogViewport({ children, className, ...props }) {
  const { setViewportEl } = useContext(ProteinViewerDialogContext)

  return (
    <div
      className={cn(
        "w-full h-full [&_canvas]:!bg-[var(--viewer-bg)] rounded-md relative",
        className,
      )}
      {...props}
      ref={node => setViewportEl(node)}
    >
      {children}
    </div>
  )
}

/** @param {React.ComponentProps<typeof DialogFooter>} props */
function ProteinViewerDialogFooter({ children, className, ...props }) {
  return (
    <DialogFooter className={cn("", className)} {...props}>
      {children}
    </DialogFooter>
  )
}

/** @param {React.ComponentProps<"div">} props */
function ProteinViewerDialogActionGroup({ children, className, ...props }) {
  return (
    <div className={cn("flex gap-2", className)} {...props}>
      <TooltipProvider>
        {children}
      </TooltipProvider>
    </div>
  )
}

/**
  * @typedef {Object} ProteinViewerDialogActionExtraProps
  * @property {string} [tooltip]
  */

/** @param {React.ComponentProps<typeof Button> & ProteinViewerDialogActionExtraProps} props */
function ProteinViewerDialogAction({ children, className, tooltip, ...props }) {
  const actionButton = (
    <Button
      size="icon-sm"
      variant="ghost"
      className={cn("text-muted-foreground", className)}
      {...props}
    >
      {children}
    </Button>
  )

  if (tooltip != null) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {actionButton}
        </TooltipTrigger>

        <TooltipContent className="bg-muted mb-2">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return actionButton
}

/**
  * @typedef {Object} ProteinViewerDialogToggleActionExtraProps
  * @property {string} [tooltip]
  */

/** @param {React.ComponentProps<typeof Toggle> & ProteinViewerDialogToggleActionExtraProps} props */
function ProteinViewerDialogToggleAction({ children, className, tooltip, ...props }) {
  const actionToggle = (
    <Toggle
      size="sm"
      variant="ghost"
      className={cn("bg-transparent text-muted-foreground", className)}
      {...props}
    >
      {children}
    </Toggle>
  )

  if (tooltip != null) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {actionToggle}
        </TooltipTrigger>

        <TooltipContent className="bg-muted mb-2">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return actionToggle
}

/** @param {React.ComponentProps<'div'>} props */
function ProteinViewerDialogViewportWithPanel({ children, className, ...props }) {
  return (
    <div
      className={cn("flex flex-col md:flex-row gap-4 min-h-0 overflow-hidden flex-1", className)}
      {...props}
    >
      {children}
    </div>
  )
}

/** @param {React.ComponentProps<'div'>} props */
function ProteinViewerDialogSidePanel({ children, className, ...props }) {
  return (
    <div
      className={cn("w-full md:w-64 flex flex-col overflow-y-auto flex-[0_1_50%] md:flex-initial md:shrink-0", className)}
      {...props}
    >
      {children}
    </div>
  )
}

ProteinViewerDialog.Trigger = ProteinViewerDialogTrigger
ProteinViewerDialog.Content = ProteinViewerDialogContent
ProteinViewerDialog.Header = ProteinViewerDialogHeader
ProteinViewerDialog.Title = ProteinViewerDialogTitle
ProteinViewerDialog.Description = ProteinViewerDialogDescription
ProteinViewerDialog.Viewport = ProteinViewerDialogViewport
ProteinViewerDialog.Footer = ProteinViewerDialogFooter
ProteinViewerDialog.ActionGroup = ProteinViewerDialogActionGroup
ProteinViewerDialog.Action = ProteinViewerDialogAction
ProteinViewerDialog.ToggleAction = ProteinViewerDialogToggleAction
ProteinViewerDialog.ViewportWithPanel = ProteinViewerDialogViewportWithPanel
ProteinViewerDialog.SidePanel = ProteinViewerDialogSidePanel

export {
  useProteinViewerDialogContext,

  DEFAULT_REPRESENTATIONS,

  ProteinViewerDialog,
  ProteinViewerDialogTrigger,
  ProteinViewerDialogContent,
  ProteinViewerDialogHeader,
  ProteinViewerDialogTitle,
  ProteinViewerDialogDescription,
  ProteinViewerDialogViewport,
  ProteinViewerDialogFooter,
  ProteinViewerDialogActionGroup,
  ProteinViewerDialogAction,
  ProteinViewerDialogToggleAction,
  ProteinViewerDialogViewportWithPanel,
  ProteinViewerDialogSidePanel,
}
